import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useUser } from "../../utils/userContext";
import useHandleErrors from '../../utils/user.utils';
import { ToastContainer, toast } from "react-toastify";
import useConfiguration from '../../utils/ConfigurationContext';
import Select from 'react-select';


const SubmitRequestForm = () => {
  const { user } = useUser();
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileData, setFileData] = useState([]);
  const handleErrors = useHandleErrors();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const config = useConfiguration();
  const [employees, setEmployees] = useState([]);
  const token = localStorage.getItem("token");
  const hasFetchedRef = useRef(false);
  const [formData, setFormData] = useState({
    category: "",
    subCategory: "",
    department: "",
    assignTo: "",
    status: "",
    priority: "",
    description: "",
    employeeID: user?.EmployeeId,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  const handleReset = (e) => {
    setFormData({
      category: "",
      subCategory: "",
      department: "",
      assignTo: "",
      status: "",
      priority: "",
      description: "",
      employeeID: user?.EmployeeId,
    });
    setUploadedFiles([]);
    setFileData([]);
    setFiles([]);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
  };

  const handleDelete = (fileIndex) => {
    setUploadedFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== fileIndex)
    );
    setFileData((prevData) =>
      prevData.filter((_, index) => index !== fileIndex)
    );
  };

  const handleUpload = () => {
    if (files.length > 0) {
      files.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const binaryData = e.target.result;
          setUploadedFiles((prevFiles) => [...prevFiles, file]);
          setFileData((prevData) => [...prevData, binaryData]);
        };
        reader.readAsBinaryString(file);
      });
      setFiles([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!formData.category) {
      toast.error("Category is required.", {
        position: toast.POSITION.TOP_CENTER,
      });
      setLoading(false);
      return;
    }
    if (!formData.subCategory) {
      toast.error("Sub Category is required.", {
        position: toast.POSITION.TOP_CENTER,
      });
      setLoading(false);
      return;
    }
    if (!formData.department) {
      toast.error("Department is required.", {
        position: toast.POSITION.TOP_CENTER,
      });
      setLoading(false);
      return;
    }
    if (!formData.assignTo) {
      toast.error("Assign To is required.", {
        position: toast.POSITION.TOP_CENTER,
      });
      setLoading(false);
      return;
    }
    if (!formData.status) {
      toast.error("State is required.", {
        position: toast.POSITION.TOP_CENTER,
      });
      setLoading(false);
      return;
    }
    if (!formData.priority) {
      toast.error("Priority is required.", {
        position: toast.POSITION.TOP_CENTER,
      });
      setLoading(false);
      return;
    }
    if (!formData.description) {
      toast.error("Description is required.", {
        position: toast.POSITION.TOP_CENTER,
      });
      setLoading(false);
      return;
    }

    const submitData = new FormData();
    for (const key in formData) {
      submitData.append(key, formData[key]);
    }

    uploadedFiles.forEach((file) => {
      submitData.append("SupportRequestDocument", file);
    });

    try {
      const path = "/supportAndRequest";
      const URL = process.env.REACT_APP_BE_URL + path;
      const response = await axios.post(URL, submitData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.status === 200) {
        setShowModal(true);
        setFormData({
          category: "",
          subCategory: "",
          department: "",
          assignTo: "",
          status: "",
          priority: "",
          description: "",
          employeeID: user?.EmployeeId,
        });
        setUploadedFiles([]);
        setFileData([]);
        setFiles([]);
      } else {
        handleErrors(response);
      }
    } catch (error) {
      if (error.response) {
        handleErrors(error.response);
      } else {
        toast.error("Something went wrong. Please try again later.", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const categoryOptions = config?.supportAndRequests?.categoryTypes?.map(category => ({
    value: category,
    label: category,
  }));
  const subCategoryOptions = config?.supportAndRequests?.subCategory?.map(category => ({
    value: category,
    label: category,
  }));

  const departmentOptions = config?.employeeManagement?.department?.map(department => ({
    value: department,
    label: department,
  }));

  const stateOptions = [
    { value: "New", label: "New" },
    { value: "InProgress", label: "In Progress" },
    { value: "Resolved", label: "Resolved" },
  ];

  const priorityOptions = [
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ];

  const ClearIndicator = () => null;

  useEffect(() => {
    if (!hasFetchedRef.current) {
      fetchEmployees();
      hasFetchedRef.current = true;
    }
  }, []);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const path = "/getEmployeeMasterReport";
      const URL = process.env.REACT_APP_BE_URL + path;
      const response = await axios.get(URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 200) {
        const activeEmployees = response.data.onboardEmployeeDetails.filter(emp => emp.Status === 'Active');
        setEmployees(activeEmployees);
      } else {
        handleErrors(response);
      }
    } catch (error) {
      if (error.response) {
        handleErrors(error.response);
      } else {
        toast.error("Something went wrong. Please try again later.", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const assignToOptions = employees.map((emp) => ({
    value: emp.OfficialEmail,
    label: `${emp.OfficialEmail}`,
  }));

  return (
    <div>
      {loading && (
        <div id="preloading">
          <span className="loader"></span>
        </div>
      )}
      <ToastContainer />
      <div className="content-page">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col">
              <h3 className="title mb-2">Support & Request</h3>
            </div>
            <div className="col-auto">
              <Link
                to={"/Complaint"}
                data-extra-toggle="tab"
                data-target="#organisation-pan"
                className="btn btn-primary mt-lg-0 mt-4"
              >
                <i className="ri-message-3-line"></i> Complaint and Feedback
              </Link>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-8">
              <div className="card mb-4">
                <div className="card-body">
                  <h5 className="title">
                    Submit Request{" "}
                    <small className="float-right">
                      Employee ID - <span>{user?.EmployeeId}</span>
                    </small>
                  </h5>
                  <hr />
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="form-group col-md-6 col-lg-6">
                        <label htmlFor="category" className="required">
                          Category
                        </label>
                        <Select
                          className="react-select-container"
                          classNamePrefix="react-select"
                          name="category"
                          value={categoryOptions?.find(option => option.value === formData.category) || null}
                          onChange={(selectedOption) =>
                            setFormData({
                              ...formData,
                              category: selectedOption ? selectedOption.value : '',
                            })
                          }
                          options={categoryOptions}
                          placeholder="select..."
                          isClearable
                          components={{ ClearIndicator }}
                          menuPortalTarget={document.body}
                        />
                      </div>
                      <div className="form-group col-md-6 col-lg-6">
                        <label htmlFor="subCategory" className="required">
                          Sub Category
                        </label>
                        <Select
                          className="react-select-container"
                          classNamePrefix="react-select"
                          name="subCategory"
                          value={subCategoryOptions?.find(option => option.value === formData.subCategory) || null}
                          onChange={(selectedOption) =>
                            setFormData({
                              ...formData,
                              subCategory: selectedOption ? selectedOption.value : '',
                            })
                          }
                          options={subCategoryOptions}
                          placeholder="select..."
                          isClearable
                          components={{ ClearIndicator }}
                          menuPortalTarget={document.body}
                        />
                      </div>
                      <div className="form-group col-md-6 col-lg-6">
                        <label htmlFor="department" className="required">
                          Department
                        </label>
                        <Select
                          className="react-select-container"
                          classNamePrefix="react-select"
                          name="department"
                          value={departmentOptions?.find(option => option.value === formData.department) || null}
                          onChange={(selectedOption) =>
                            setFormData({
                              ...formData,
                              department: selectedOption ? selectedOption.value : '',
                            })
                          }
                          options={departmentOptions}
                          placeholder="Choose..."
                          isClearable
                          components={{ ClearIndicator }}
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 }),
                            control: base => ({ ...base, zIndex: 1 })
                          }}
                        />
                      </div>
                      <div className="form-group col-md-6 col-lg-6">
                        <label htmlFor="assignTo" className="required">
                          Assign to
                        </label>
                        <Select
                          className="react-select-container"
                          classNamePrefix="react-select"
                          name="assignTo"
                          value={assignToOptions?.find(option => option.value === formData.assignTo) || null}
                          onChange={(selectedOption) =>
                            setFormData({
                              ...formData,
                              assignTo: selectedOption ? selectedOption.value : '',
                            })
                          }
                          options={assignToOptions}
                          placeholder="select..."
                          isClearable
                          components={{ ClearIndicator }}
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 }),
                            control: base => ({ ...base, zIndex: 1 })
                          }}
                        />
                      </div>
                      <div className="form-group col-md-6 col-lg-6">
                        <label htmlFor="state" className="required">
                          State
                        </label>
                        <Select
                          className="react-select-container"
                          classNamePrefix="react-select"
                          name="status"
                          value={stateOptions.find(option => option.value === formData.status) || null}
                          onChange={selectedOption =>
                            setFormData({
                              ...formData,
                              status: selectedOption ? selectedOption.value : '',
                            })
                          }
                          options={stateOptions}
                          placeholder="select..."
                          isClearable
                          components={{ ClearIndicator }}
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 }),
                            control: base => ({ ...base, zIndex: 1 })
                          }}
                        />
                      </div>
                      <div className="form-group col-md-6 col-lg-6">
                        <label htmlFor="priority" className="required">
                          Priority
                        </label>
                        <Select
                          className="react-select-container"
                          classNamePrefix="react-select"
                          name="priority"
                          value={priorityOptions.find(option => option.value === formData.priority) || null}
                          onChange={selectedOption =>
                            setFormData({
                              ...formData,
                              priority: selectedOption ? selectedOption.value : '',
                            })
                          }
                          options={priorityOptions}
                          placeholder="select..."
                          isClearable
                          components={{ ClearIndicator }}
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 }),
                            control: base => ({ ...base, zIndex: 1 })
                          }}
                        />
                      </div>
                      <div className="col-lg-12 col-md-12">
                        <div className="form-group">
                          <label htmlFor="Document">
                            Document
                          </label>
                          <div className="d-flex align-items-center">
                            <div className="custom-file mr-2">
                              <input
                                type="file"
                                className="custom-file-input"
                                id="Document"
                                multiple
                                onChange={handleFileChange}
                              />
                              <label className="custom-file-label" htmlFor="Document">
                                {files.length > 0 ? `${files.length} files selected` : 'Choose a file'}
                              </label>
                            </div>
                            <button className="btn btn-secondary" type="button" onClick={handleUpload}>
                              Upload
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-12 col-md-12">
                        <div className="container-fluid my-2 px-0">
                          <div className="row">
                            {uploadedFiles?.map((file, index) => (
                              <div key={index} className="col-6 mb-2">
                                <button
                                  className="btn btn-link text-danger btn-action"
                                  type="button"
                                  onClick={() => handleDelete(index)}
                                >
                                  <i className="ri-delete-bin-6-line" />
                                </button>
                                <a href="#" onClick={() => window.open(URL.createObjectURL(file), '_blank')}>
                                  {file.name}
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="form-group col-md-12 col-lg-12">
                        <label htmlFor="description" className="required">
                          Description
                        </label>
                        <textarea
                          name="description"
                          className="form-control"
                          rows="5"
                          value={formData.description}
                          onChange={handleChange}
                        ></textarea>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="card ww-app-bg">
                <div className="card-body">
                  <span>
                    <i className="ri-customer-service-2-fill"></i>
                  </span>
                </div>
              </div>
              <div className="card mb-4">
                <div className="card-body">
                  <h4 className="mb-2">Know about your query?</h4>
                  <p>
                    Click the below icon and refer all our policies related to
                    your needs.
                  </p>
                  <div className="row align-items-center justify-content-center">
                    <div className="col-auto text-center">
                      <Link
                        to={"/policies"}
                        className="info-icon-box"
                        title="Policies"
                        rel="tooltip"
                      >
                        <i className="ri-file-text-line"></i>
                      </Link>
                    </div>
                  </div>
                  <div className="row align-items-center border-top">
                    <div className="col-12">
                      <p className="mt-3">
                        Quickly get in touch with us for more queries.
                      </p>
                    </div>
                  </div>
                  <div className="row mt-2">
                    <div className="col-1">
                      <i className="ri-mail-send-fill"></i>
                    </div>
                    <div className="col">{config?.supportEmail}</div>
                  </div>
                  <div className="row mt-2">
                    <div className="col-1">
                      <i className="ri-message-3-line"></i>
                    </div>
                    <div className="col">Chat with us</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="row fixed-footer">
        <div className="col-lg-12 text-center">
          <button className="btn btn-light" type="reset" onClick={handleReset}>
            Reset
          </button>
          <button
            className="btn btn-primary"
            type="submit"
            onClick={handleSubmit}
          >
            Submit <i className="ri-checkbox-circle-line"></i>
          </button>
        </div>
      </div>
      {showModal && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-labelledby="successModalLabel" aria-hidden="true">
          <div className="modal-dialog modal-sm modal-dialog-centered" role="document">
            <div className="modal-content shadow success-modal">
              <div className="modal-header py-2">
                <h6 className="modal-title" id="successModalModalLabel">Success!</h6>
                <button className="close" type="button" aria-label="Close" onClick={handleCloseModal}>
                  <span aria-hidden="true"><i className="ri-close-circle-fill font-size-20"></i></span>
                </button>
              </div>
              <div className="modal-body pb-5">
                <div className="row">
                  <div className="col-12 text-success text-center"><i className="ri-checkbox-circle-fill"></i></div>
                  <div className="col-12 text-center">Request submitted successfully.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmitRequestForm;
